// saveOrShareBlob.js — v242.6.0
// Cross-platform "save this generated file" for blobs (e.g. the records zip).
//
// Native (iOS/Android via Capacitor): the WKWebView has no Downloads folder, so
// the web a.download/a.click() path silently no-ops on device. Instead we write
// the blob into the app cache dir and open the system Share sheet, which gives
// the user "Save to Files" plus share targets.
//
// Web: standard browser blob download (unchanged behavior).
//
// The Capacitor plugins are imported dynamically so the web bundle never needs
// them and only loads them on a real device.

import { Capacitor } from '@capacitor/core'

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const res = String(reader.result || '')
      const comma = res.indexOf(',')
      resolve(comma >= 0 ? res.slice(comma + 1) : res)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

// Returns { method: 'share' | 'download' } so callers can tailor copy if needed.
//
// ★ v242.88.0 — `opts` is optional and every existing call site is unchanged.
//   `opts.text` rides along in the share sheet so a shared FILE can carry a
//   MESSAGE: TF b34 #14 asked that "Share with doctor" send the document, and
//   the doctor still needs the share code in the body to claim the case.
//   Without this, a shared file arrives with no context and a shared message
//   arrives with no document — which is what the two buttons were doing.
export async function saveOrShareBlob(blob, filename, opts = {}) {
  if (Capacitor.isNativePlatform()) {
    const [{ Filesystem, Directory }, { Share }] = await Promise.all([
      import('@capacitor/filesystem'),
      import('@capacitor/share'),
    ])
    const data = await blobToBase64(blob)
    // Write into the cache dir (transient is fine; the user saves a copy via Share).
    await Filesystem.writeFile({ path: filename, data, directory: Directory.Cache })
    const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache })
    // A dismissed share sheet rejects — that's not an error for us; the file is
    // already written. Swallow it so the caller still lands on the success state.
    try {
      await Share.share({
        title: opts.title || filename,
        url: uri,
        ...(opts.text ? { text: opts.text } : {}),
        ...(opts.dialogTitle ? { dialogTitle: opts.dialogTitle } : {}),
      })
    } catch (_) { /* user dismissed the share sheet */ }
    return { method: 'share' }
  }

  // Web: classic blob download.
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 5000)
  return { method: 'download' }
}
